import { ChevronDown, ExternalLink } from 'lucide-react';
import type { SyntheticEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  cn,
  InfoHint,
  Popover,
  PopoverContent,
  PopoverTrigger,
  type DataGridConfig,
} from '@eo/shared';

import type {
  CapacityClientRow,
  CapacityEntryRow,
  ClientReport,
  FinishedTask,
  StaffReport,
} from '../api/types';
import {
  BILLABLE_OPTIONS,
  BUDGET_BUCKETS,
  CLIENT_COLUMN_HELP,
  CLIENT_GRID_PREFIX,
  LOG_GRID_PREFIX,
  ROLE_BAR_CLASS,
  TERM_HELP,
} from '../constants';
import { ForecastBreakdown } from '../ForecastBreakdown';
import { formatHours, stopRowEvent } from '../helpers';
import { LoadBar } from '../LoadBar';
import { LoggedBreakdown } from '../LoggedBreakdown';
import { ClientPlanDialog } from '../ClientPlanDialog';
import { TeamCell } from '../TeamCell';
import type {
  LogFilters,
  OnPlan,
  ClientCardProps,
  ProjectedOverCellProps,
  SearchFilterSource,
  TeamCategoryRow,
} from '../types';

function TaskWithLink({ row }: { row: CapacityEntryRow }) {
  return (
    <span className="flex max-w-72 items-center gap-2">
      <span className="truncate">{row.taskName}</span>
      {row.taskId && (
        <a
          href={`https://app.clickup.com/t/${row.taskId}`}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${row.taskName} in ClickUp`}
          className="shrink-0 text-text-tertiary hover:text-text-primary"
        >
          <ExternalLink className="size-3.5" />
        </a>
      )}
    </span>
  );
}

type GridFilter = NonNullable<DataGridConfig<unknown>['filters']>[number];

function searchFilter(
  id: string,
  header: string,
  allLabel: string,
  source: SearchFilterSource,
): GridFilter {
  return { id, header, filter: { type: 'search', allLabel, ...source } };
}

// Column ids double as the server's sort keys; the person, client and billable
// filters ride the same prefixed URL params the server reads.
export function makeEntriesGridConfig({
  people,
  clients,
}: LogFilters): DataGridConfig<CapacityEntryRow> {
  return {
    getRowId: (row) => row.entryId,
    paramPrefix: LOG_GRID_PREFIX,
    rowTestId: 'entry-row',
    search: { placeholder: 'Search task' },
    filters: [
      ...(people ? [searchFilter('member', 'Person', 'Everyone', people)] : []),
      ...(clients ? [searchFilter('client', 'Client', 'All clients', clients)] : []),
    ],
    emptyState: { title: 'No ClickUp time entries match.' },
    columns: [
      {
        id: 'date',
        header: 'Date',
        sortable: true,
        accessor: (row) => row.startTs,
        cell: (row) => (
          <span className="text-sm text-text-tertiary">
            {new Date(row.startTs).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              timeZone: 'America/New_York',
            })}
          </span>
        ),
      },
      { id: 'member', header: 'Person', sortable: true, accessor: (row) => row.member },
      {
        id: 'client',
        header: 'Client',
        sortable: true,
        accessor: (row) => row.clientName,
        cell: (row) =>
          row.clientName ?? <span className="text-text-tertiary">No client · {row.listName}</span>,
      },
      {
        id: 'task',
        header: 'Task',
        accessor: (row) => row.taskName,
        cell: (row) => <TaskWithLink row={row} />,
      },
      {
        id: 'status',
        header: 'Status',
        accessor: (row) => row.taskStatus,
        cell: (row) => <span className="text-sm text-text-tertiary">{row.taskStatus ?? '—'}</span>,
      },
      {
        id: 'billable',
        header: 'Billing',
        accessor: (row) => row.billable,
        filter: { type: 'select', options: BILLABLE_OPTIONS, allLabel: 'All hours' },
        cell: (row) => (row.billable ? 'Billable' : 'Non-billable'),
      },
      {
        id: 'hours',
        header: 'Hours',
        align: 'right',
        sortable: true,
        accessor: (row) => row.durationHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.durationHours)}</span>,
      },
    ],
  };
}

function NameWithAssignment({ name, assigned }: { name: string; assigned: boolean }) {
  return (
    <span className="flex items-center gap-2">
      {name}
      {!assigned && <Badge variant="outline">Not assigned</Badge>}
    </span>
  );
}

export type ClientMemberRow = ClientReport['assignments'][number];

type ClientMembersGridArgs = { staffName: (userId: string) => string };

export function makeClientMembersGridConfig({
  staffName,
}: ClientMembersGridArgs): DataGridConfig<ClientMemberRow> {
  const varianceCell = (value: number) => (
    <span className={cn('tabular-nums', value > 0 ? 'text-destructive' : 'text-success')}>
      {value > 0 ? '+' : ''}
      {formatHours(value)}
    </span>
  );
  const variance = (row: ClientMemberRow) => row.actualHours - (row.fixedHours + row.variableHours);

  return {
    pagination: false,
    getRowId: (row) => row.userId,
    paramPrefix: 'mem_',
    rowTestId: 'client-member-row',
    emptyState: { title: 'No hour assignments configured for this client.' },
    columns: [
      {
        id: 'member',
        header: 'Member',
        accessor: (row) => staffName(row.userId),
        cell: (row) => <NameWithAssignment name={staffName(row.userId)} assigned={row.assigned} />,
      },
      {
        id: 'fixedHours',
        header: 'Assigned fixed',
        align: 'right',
        accessor: (row) => row.fixedHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.fixedHours)}</span>,
      },
      {
        id: 'variableHours',
        header: 'Assigned variable',
        align: 'right',
        accessor: (row) => row.variableHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.variableHours)}</span>,
      },
      {
        id: 'actualHours',
        header: 'Actual',
        align: 'right',
        accessor: (row) => row.actualHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.actualHours)}</span>,
      },
      {
        id: 'variance',
        header: 'Variance',
        align: 'right',
        accessor: variance,
        // Nothing was assigned, so there is no plan to vary from.
        cell: (row) => (row.assigned ? varianceCell(variance(row)) : '—'),
      },
    ],
  };
}

export function makeTeamCategoriesGridConfig(): DataGridConfig<TeamCategoryRow> {
  const dash = (value: number | null, render: (v: number) => string) =>
    value === null ? '—' : render(value);

  return {
    getRowId: (row) => row.id,
    pagination: false,
    paramPrefix: 'cat_',
    rowTestId: 'team-category-row',
    emptyState: { title: 'No categories.' },
    columns: [
      { id: 'label', header: 'Category', accessor: (row) => row.label },
      {
        id: 'splitPct',
        header: 'Split',
        align: 'right',
        accessor: (row) => row.splitPct,
        cell: (row) => <span className="tabular-nums">{dash(row.splitPct, (v) => `${v}%`)}</span>,
      },
      {
        id: 'capacityShare',
        header: 'Capacity share',
        align: 'right',
        accessor: (row) => row.capacityShare,
        cell: (row) => <span className="tabular-nums">{dash(row.capacityShare, formatHours)}</span>,
      },
      {
        id: 'actual',
        header: 'Actual',
        align: 'right',
        accessor: (row) => row.actual,
        cell: (row) => <span className="tabular-nums">{formatHours(row.actual)}</span>,
      },
    ],
  };
}

export type TeamClientRow = StaffReport['clientBreakdown'][number];

type TeamClientsGridArgs = { clientName: (id: string) => string };

export function makeTeamClientsGridConfig({
  clientName,
}: TeamClientsGridArgs): DataGridConfig<TeamClientRow> {
  const hours = (value: number) => <span className="tabular-nums">{formatHours(value)}</span>;

  return {
    pagination: false,
    getRowId: (row) => row.clientId,
    paramPrefix: 'tcl_',
    rowTestId: 'team-client-row',
    emptyState: { title: 'No client hours this month.' },
    columns: [
      {
        id: 'client',
        header: 'Client',
        accessor: (row) => clientName(row.clientId),
        cell: (row) => (
          <NameWithAssignment name={clientName(row.clientId)} assigned={row.assigned} />
        ),
      },
      {
        id: 'assignedFixed',
        header: 'Assigned fixed',
        align: 'right',
        accessor: (row) => row.assignedFixed,
        cell: (row) => hours(row.assignedFixed),
      },
      {
        id: 'assignedVariable',
        header: 'Assigned variable',
        align: 'right',
        accessor: (row) => row.assignedVariable,
        cell: (row) => hours(row.assignedVariable),
      },
      {
        id: 'actualFixed',
        header: 'Actual fixed',
        align: 'right',
        accessor: (row) => row.actualFixed,
        cell: (row) => hours(row.actualFixed),
      },
      {
        id: 'actualVariable',
        header: 'Actual variable',
        align: 'right',
        accessor: (row) => row.actualVariable,
        cell: (row) => hours(row.actualVariable),
      },
    ],
  };
}

// Fixed and variable each against their own budget: variable is the billable
// side, so a client on budget in total can still be over where it is billed.
function LoggedByBucket({ row }: { row: CapacityClientRow }) {
  return (
    <div className="flex min-w-48 flex-col gap-2">
      {BUDGET_BUCKETS.map(({ key, label }) => {
        const bucket = row[key];
        const actualKey = key === 'fixed' ? 'actualFixed' : 'actualVariable';
        const byCfo = row.byRole.cfo[actualKey];
        const byData = row.byRole.dataTeam[actualKey];
        return (
          <div key={key} className="flex flex-col gap-1">
            <LoadBar
              label={`${row.name} ${key}: ${formatHours(bucket.actual)} logged of ${formatHours(bucket.budget)} budget, ${formatHours(byCfo)} by the CFO and ${formatHours(byData)} by the Data team`}
              scale={Math.max(bucket.budget, bucket.actual)}
              target={bucket.budget}
              targetHint={`Budget ${formatHours(bucket.budget)}: the ${key} hours the client pays for this month, extra hours included. Hours logged past this line are overage.`}
              segments={[
                { key: 'cfo', hours: byCfo, className: ROLE_BAR_CLASS.cfo.logged },
                { key: 'dataTeam', hours: byData, className: ROLE_BAR_CLASS.dataTeam.logged },
              ]}
              details={
                <LoggedBreakdown
                  byCfo={byCfo}
                  byData={byData}
                  logged={bucket.actual}
                  budget={bucket.budget}
                />
              }
            />
            <span className="text-xs tabular-nums text-text-tertiary">
              {label} {formatHours(bucket.actual)} of {formatHours(bucket.budget)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

type ClientCapacityGridArgs = {
  clientHref: (clientId: string) => string;
  clients: SearchFilterSource;
  people: SearchFilterSource;
  onPlan: OnPlan;
  // Only the all-portfolios view mixes owners; inside one, the owner is a given.
  showOwner: boolean;
};

// Column ids double as the server's sort keys, so every sortable column here is
// one the route can order by. A row opens that client's detail on the page.
export function makeClientCapacityGridConfig({
  clientHref,
  clients,
  people,
  onPlan,
  showOwner,
}: ClientCapacityGridArgs): DataGridConfig<CapacityClientRow> {
  return {
    getRowId: (row) => row.clientId,
    rowLink: (row) => clientHref(row.clientId),
    paramPrefix: CLIENT_GRID_PREFIX,
    rowTestId: 'client-capacity-row',
    card: (row) => (
      <ClientCard
        row={row}
        clientHref={clientHref(row.clientId)}
        people={people}
        onPlan={onPlan}
        showOwner={showOwner}
      />
    ),
    filters: [searchFilter('client', 'Client', 'All clients', clients)],
    emptyState: { title: 'No clients match this filter.' },
    columns: withColumnHelp([
      { id: 'name', header: 'Client', sortable: true, accessor: (row) => row.name },
      ...(showOwner
        ? [
            {
              id: 'ownerName',
              header: 'Owner',
              accessor: (row: CapacityClientRow) => row.ownerName,
            },
          ]
        : []),
      {
        id: 'plan_team',
        header: 'Team',
        accessor: (row) => row.plan.team.length,
        cell: (row) => (
          <span className="flex max-w-72 min-w-40 items-start justify-between gap-2">
            <TeamCell row={row} />
            <ClientPlanDialog row={row} people={people} onPlan={onPlan} />
          </span>
        ),
      },
      {
        id: 'actual',
        header: 'Logged',
        sortable: true,
        accessor: (row) => row.actual,
        cell: (row) => <LoggedByBucket row={row} />,
      },
      {
        id: 'overage',
        header: 'Overage',
        align: 'right',
        sortable: true,
        accessor: (row) => row.overage,
        cell: (row) => (
          <span className="flex flex-col items-end gap-1">
            <VariableOverageBadge row={row} clientHref={clientHref(row.clientId)} />
            {row.fixed.overage > 0 && <OverageCell row={row} />}
          </span>
        ),
      },
      {
        id: 'projectedOver',
        header: 'Projected over',
        align: 'right',
        sortable: true,
        accessor: (row) => row.projectedOver,
        cell: (row) => (
          <ProjectedOverCell
            name={row.name}
            forecast={row.forecast}
            clientHref={clientHref(row.clientId)}
          />
        ),
      },
    ]),
  };
}

// Variable is what the excess hours bill charges, so its overage opens the
// client's bill.
// The fixed overage is absorbed, so it stays at the card's foot; the variable
// one is billed and sits beside the name.
function OverageCell({ row }: { row: CapacityClientRow }) {
  return row.fixed.overage > 0 ? (
    <span className="tabular-nums text-destructive">
      Fixed {formatHours(row.fixed.overage)} over
    </span>
  ) : (
    <span className="text-text-tertiary">—</span>
  );
}

// Variable is what the excess hours bill charges. The bill is drawn up once at
// month end, so its overage opens the client's summary, where it shows why.
function VariableOverageBadge({ row, clientHref }: { row: CapacityClientRow; clientHref: string }) {
  if (row.variable.overage <= 0) return null;
  return (
    <Link
      to={clientHref}
      aria-label={`Review ${row.name}’s ${formatHours(row.variable.overage)} variable overage`}
      onClick={stopRowEvent}
      onKeyDown={stopRowEvent}
      className="shrink-0 whitespace-nowrap rounded-full bg-badge-error px-2 py-0.5 text-xs font-medium tabular-nums text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus"
    >
      Variable {formatHours(row.variable.overage)} over
    </Link>
  );
}

// A client at a glance: its buckets against their budgets, who holds it, and
// how far over it is or is heading. Planning it opens from its corner.
function ClientCard({ row, clientHref, people, onPlan, showOwner }: ClientCardProps) {
  return (
    <div className="flex flex-1 flex-col gap-3">
      <div data-testid="client-card-header" className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <h3 className="truncate text-base text-text-primary">{row.name}</h3>
          {showOwner && <span className="text-xs text-text-tertiary">{row.ownerName}</span>}
        </div>
        <span className="flex items-center gap-1">
          <VariableOverageBadge row={row} clientHref={clientHref} />
          <ClientPlanDialog row={row} people={people} onPlan={onPlan} />
        </span>
      </div>
      <LoggedByBucket row={row} />
      <TeamCell row={row} />
      {/* Its help, bill link and forecast act on their own; none of them opens the client. */}
      <div
        onClick={stopRowEvent}
        onKeyDown={stopRowEvent}
        className="mt-auto flex items-end justify-between gap-2 border-t border-border-faint pt-2 text-xs"
      >
        <span className="flex flex-col gap-0.5">
          <span className="flex items-center gap-1 text-text-tertiary">
            Overage
            <InfoHint term="Overage">{TERM_HELP.overage}</InfoHint>
          </span>
          <OverageCell row={row} />
        </span>
        <span className="flex flex-col items-end gap-0.5">
          <span className="flex items-center gap-1 text-text-tertiary">
            Projected over
            <InfoHint term="Projected over">{TERM_HELP.projectedOver}</InfoHint>
          </span>
          <ProjectedOverCell name={row.name} forecast={row.forecast} clientHref={clientHref} />
        </span>
      </div>
    </div>
  );
}

// The row itself links to the client, and React carries events out of the
// portalled popover to it, so clicks and keys stop here.
function ProjectedOverCell({ name, forecast, clientHref }: ProjectedOverCellProps) {
  const over = BUDGET_BUCKETS.filter(({ key }) => forecast[key].projectedOver > 0);
  const stop = (e: SyntheticEvent) => e.stopPropagation();
  return (
    <span onClick={stop} onKeyDown={stop} className="inline-flex">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${name} forecast`}
            className="flex items-center gap-1 rounded-8 border border-transparent px-1.5 py-0.5 text-right tabular-nums text-text-secondary transition-colors hover:bg-foreground/[0.06] focus-visible:border-focus focus-visible:outline-none"
          >
            <span className="flex flex-col items-end">
              {over.length > 0
                ? over.map(({ key, label }) => (
                    <span key={key}>
                      {label} {formatHours(forecast[key].projectedOver)} over
                    </span>
                  ))
                : '—'}
            </span>
            <ChevronDown className="size-3.5 shrink-0 text-text-tertiary" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="flex w-80 flex-col gap-3">
          <p className="flex items-center gap-1 text-sm text-text-primary">
            {name} · Forecast
            <InfoHint term="Forecast">{TERM_HELP.forecast}</InfoHint>
          </p>
          <ForecastBreakdown forecast={forecast} />
          <Link to={clientHref} className="text-xs text-text-secondary hover:text-text-primary">
            Open client detail →
          </Link>
        </PopoverContent>
      </Popover>
    </span>
  );
}

// A finished task's estimate against the time it took: the check Amer reads to
// see how well the team estimates.
export function makeFinishedTasksGridConfig(): DataGridConfig<FinishedTask> {
  const hours = (value: number) => <span className="tabular-nums">{formatHours(value)}</span>;
  return {
    getRowId: (row) => row.taskId,
    paramPrefix: 'fin_',
    rowTestId: 'finished-task-row',
    pagination: false,
    emptyState: { title: 'No estimated tasks finished this month.' },
    columns: [
      { id: 'name', header: 'Finished task', accessor: (row) => row.name },
      {
        id: 'estimateHours',
        header: 'Estimated',
        align: 'right',
        accessor: (row) => row.estimateHours,
        cell: (row) => hours(row.estimateHours),
      },
      {
        id: 'spentHours',
        header: 'Logged',
        align: 'right',
        accessor: (row) => row.spentHours,
        cell: (row) => hours(row.spentHours),
      },
      {
        id: 'difference',
        header: 'Difference',
        align: 'right',
        accessor: (row) => row.spentHours - row.estimateHours,
        cell: (row) => {
          const diff = row.spentHours - row.estimateHours;
          return (
            <span
              className={cn('tabular-nums', diff > 0 ? 'text-destructive' : 'text-text-tertiary')}
            >
              {diff > 0 ? '+' : ''}
              {formatHours(diff)}
            </span>
          );
        },
      },
    ],
  };
}

// Applied by column id, so a column picks up its explanation wherever it sits.
function withColumnHelp<TRow>(
  columns: DataGridConfig<TRow>['columns'],
): DataGridConfig<TRow>['columns'] {
  return columns.map((column) => ({ ...column, info: CLIENT_COLUMN_HELP[column.id] }));
}
